import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { useTranslate } from 'shared/lib/i18n'

/**
 * The Box calls of the hub's bench and their words (tickets 11 + 29) — what the
 * components of `ui/HubBoxes.tsx` and `ui/HubReceiving.tsx` share.
 */

type State = RouterOutputs['hubReceiving']['State']
export type HubBoxCard = State['boxes'][number]
export type HubIdentifyAnswer = RouterOutputs['hubReceiving']['Identify']
export type HubReceiveBoxRequest = RouterInputs['hubReceiving']['ReceiveBox']
export type HubReceiveBoxAnswer = RouterOutputs['hubReceiving']['ReceiveBox']
export type HubOpenBoxRequest = RouterInputs['hubReceiving']['OpenBox']
export type HubOpenBoxAnswer = RouterOutputs['hubReceiving']['OpenBox']
export type HubFinishBoxAnswer = RouterOutputs['hubReceiving']['FinishBox']
type BoxRefusal = Extract<HubReceiveBoxAnswer, { outcome: 'refused' }>
type MustOpenReason = HubBoxCard['mustOpen'][number]

/** The Box calls of the bench. */
export interface HubBoxSeams {
	identify?: (input: { manifestId: string; code: string; boxId?: string }) => Promise<HubIdentifyAnswer>
	receiveBox?: (input: HubReceiveBoxRequest) => Promise<HubReceiveBoxAnswer>
	openBox?: (input: HubOpenBoxRequest) => Promise<HubOpenBoxAnswer>
	finishBox?: (input: { manifestId: string; containerId: string }) => Promise<HubFinishBoxAnswer>
}

export const boxCallsViaTrpc: Required<HubBoxSeams> = {
	identify: (input) => trpcClient.hubReceiving.Identify.query(input),
	receiveBox: (input) => trpcClient.hubReceiving.ReceiveBox.mutate(input),
	openBox: (input) => trpcClient.hubReceiving.OpenBox.mutate(input),
	finishBox: (input) => trpcClient.hubReceiving.FinishBox.mutate(input),
}

/** What the bench shows after an act on a Box: a coloured line, as for a scan. */
export interface BoxBanner {
	tone: 'ready' | 'attention' | 'refused'
	outcome: string
	title: string
	detail: string
	/** Codes listed under it — a Box's shortage or extras. */
	shortage?: string[]
	extras?: string[]
}

/** A thrown call, in words. */
export const boxFailure = (error: unknown) => (error instanceof Error ? error.message : String(error))

/** The words of a Box's must-open reasons. */
export function useMustOpenWords() {
	const { t } = useTranslate()
	return (reasons: readonly MustOpenReason[]) =>
		reasons.map((r) => t(`hubBox.mustOpen_${r}`) || r).join(', ')
}

/** The words of a refused act on a Box. */
export function useBoxRefusalWords() {
	const { t } = useTranslate()
	const mustOpen = useMustOpenWords()
	return (answer: BoxRefusal): BoxBanner => ({
		tone: 'refused',
		outcome: 'refused',
		title: t('hubBox.refusedTitle', { code: answer.code || '—' }),
		detail:
			answer.reason === 'must_open' && answer.mustOpen?.length
				? t('hubBox.refusedMustOpen', { reasons: mustOpen(answer.mustOpen) })
				: t(`hubBoxRefusal.${answer.reason}`) || answer.reason,
	})
}

